import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Calendar,
  Building2,
  Home,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import BackNavigationButton from "../components/navigation/BackNavigationButton";
import { toast } from "sonner";
import { api } from "../services/apiClient";
const DEFAULT_EXCHANGE_RATE = 20;

const DevelopmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [development, setDevelopment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchDevelopment();
  }, [id]);

  const fetchDevelopment = async () => {
    try {
      const response = await api.get(`/developments/${id}`, { auth: false });
      if (!response.ok) throw new Error("No encontrado");
      const data = await response.json();
      setDevelopment(data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al cargar desarrollo");
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case "En Planeación":
        return "bg-amber-100 text-amber-800";
      case "En Construcción":
        return "bg-blue-100 text-blue-800";
      case "Entregado":
        return "bg-emerald-100 text-emerald-800";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const scrollToUnits = () => {
    const unitsSection = document.getElementById("development-properties-section");
    if (unitsSection) {
      unitsSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex justify-center items-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!development) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="text-center py-32">
          <h2 className="text-2xl font-bold mb-4">Desarrollo no encontrado</h2>
          <Button onClick={() => navigate("/developments-list")}>
            Ver Desarrollos
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const images = [
    development.cover_image_url,
    ...(development.image_urls || []),
  ].filter(Boolean);

  const amenities = Array.isArray(development.amenities)
    ? development.amenities
    : [];
  const properties = development.properties || [];

  return (
    <div className="min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-10 pb-24 lg:pb-10">
        <BackNavigationButton
          fallbackPath="/developments-list"
          label="Volver a desarrollos"
          className="mb-4"
          dataTestId="back-btn"
        />

        {/* Hero Image */}
        {images.length > 0 && (
          <div
            className="relative aspect-video md:aspect-[21/9] rounded-xl overflow-hidden mb-8 bg-muted"
            data-testid="dev-hero-image"
          >
            <img
              src={images[currentImageIndex]}
              alt={development.name}
              className="w-full h-full object-cover"
            />
            {images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImageIndex(i)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentImageIndex ? "bg-white scale-125" : "bg-white/50"}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Development Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span
                  className={`text-xs font-semibold px-3 py-1 rounded-full ${statusColor(development.status)}`}
                >
                  {development.status || "En Construcción"}
                </span>
                {development.development_type && (
                  <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                    {development.development_type}
                  </span>
                )}
              </div>
              <h1
                className="text-2xl md:text-4xl font-bold mb-2"
                style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                data-testid="dev-name"
              >
                {development.name}
              </h1>
              {development.address && (
                <p className="flex items-center text-muted-foreground">
                  <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                  {development.address}
                </p>
              )}
            </div>

            {development.description && (
              <div>
                <h2 className="text-lg font-semibold mb-2">
                  Acerca del Desarrollo
                </h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                  {development.description}
                </p>
              </div>
            )}

            {amenities.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-3">Amenidades</h2>
                <div className="flex flex-wrap gap-2">
                  {amenities.map((a, i) => (
                    <span
                      key={i}
                      className="bg-primary/5 text-primary border border-primary/20 px-3 py-1 rounded-full text-sm"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-muted/50 border border-border/40 rounded-lg p-5 space-y-4">
              <h3 className="font-semibold text-lg">Resumen</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Propiedades</span>
                  <span className="font-medium">{properties.length}</span>
                </div>
                {development.estimated_delivery && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Entrega estimada
                    </span>
                    <span className="font-medium flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {development.estimated_delivery}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Estatus</span>
                  <span className="font-medium">
                    {development.status || "En Construcción"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Linked Properties */}
        <div
          className="mb-12"
          data-testid="dev-properties-section"
          id="development-properties-section"
        >
          <div className="flex items-center gap-3 mb-6">
            <Home className="w-5 h-5 text-primary" />
            <h2
              className="text-xl md:text-2xl font-bold"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Propiedades en este Desarrollo ({properties.length})
            </h2>
          </div>

          {properties.length === 0 ? (
            <div className="text-center py-12 bg-muted/30 rounded-lg border border-dashed border-border">
              <Building2 className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-muted-foreground">
                Aún no hay propiedades vinculadas a este desarrollo.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {properties.map((property) => (
                <div
                  key={property.id}
                  onClick={() => navigate(`/property/${property.id}`)}
                  className="bg-background border border-border/40 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-primary/40 transition-all duration-300 cursor-pointer"
                  data-testid={`dev-property-card-${property.id}`}
                >
                  <div className="aspect-video overflow-hidden bg-muted">
                    <img
                      src={
                        property.image_urls?.[0] ||
                        property.cover_image_url ||
                        "https://images.pexels.com/photos/35193584/pexels-photo-35193584.jpeg"
                      }
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs uppercase tracking-wider font-semibold text-primary">
                        {property.category}
                        {property.property_type
                          ? ` · ${property.property_type}`
                          : ""}
                      </span>
                      <span className="text-xs text-muted-foreground capitalize">
                        {property.status}
                      </span>
                    </div>
                    <h3 className="text-base font-bold mb-1 line-clamp-1">
                      {property.title}
                    </h3>
                    {property.address && (
                      <p className="text-xs text-muted-foreground mb-2 flex items-center line-clamp-1">
                        <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                        {property.address}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mb-2 text-xs text-muted-foreground">
                      {property.bedrooms && (
                        <span className="flex items-center">
                          <Bed className="w-3 h-3 mr-1" />
                          {property.bedrooms}
                        </span>
                      )}
                      {property.bathrooms && (
                        <span className="flex items-center">
                          <Bath className="w-3 h-3 mr-1" />
                          {property.bathrooms}
                        </span>
                      )}
                      {property.land_size_m2 && (
                        <span className="flex items-center">
                          <Square className="w-3 h-3 mr-1" />
                          {property.land_size_m2} m²
                        </span>
                      )}
                    </div>
                    <p className="text-lg font-bold">
                      $
                      {(
                        property.price_mxn ||
                        property.price_usd * DEFAULT_EXCHANGE_RATE ||
                        0
                      ).toLocaleString()}
                      <span className="text-xs font-normal text-muted-foreground ml-1">
                        MXN
                      </span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur px-4 py-3">
        <div className="max-w-7xl mx-auto grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => navigate("/developments-list")}>Volver</Button>
          <Button onClick={scrollToUnits}>Ver propiedades</Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DevelopmentDetail;
